import {useEffect, useRef, useState} from "react";
import {getConsent, subscribeConsent} from "consent/consentStore";
import {useConsent} from "consent/useConsent";
import {campaignPaused, type CampaignId} from "home/landing/campaignPlayback";
import {reportLandingObservation} from "upsell/offerEventBeacon";
import {campaignVisits, type LandingVisit, type LandingAttribution, type CampaignSurfaceId} from "./landingVisit";
import type {CampaignOffer} from "home/landing/landingOfferPlan";
import {LandingDwell} from "./landingDwell";

/** React binds visible state; the plain visit/dwell modules own assignment, deduplication and qualification. */
export function useLandingAnalytics(surface: CampaignSurfaceId, offerPlan: CampaignOffer, {rotation, slide, ratio, hidden, obscured, dissolving, active}: {
	rotation: readonly CampaignId[] | null; slide: CampaignId; ratio: number; hidden: boolean;
	obscured: boolean; dissolving: boolean; active: boolean;
}) {
	const visit = useRef<LandingVisit | null>(null);
	const dwell = useRef(new LandingDwell());
	const heldRef = useRef<LandingAttribution | undefined>(undefined);
	const [held, setHeld] = useState<LandingAttribution | undefined>(undefined);
	const {consent} = useConsent();
	const unobscured = !campaignPaused({hidden, obscured, dissolving, inactive: !active, unassigned: rotation === null, denied: !consent.analytics});

	useEffect(() => {
		if (rotation !== null) visit.current = campaignVisits.forSurface(surface, rotation, Date.now(), offerPlan);
		dwell.current = new LandingDwell();
	}, [rotation, surface, offerPlan, consent.analytics]);
	useEffect(() => subscribeConsent(next => {
		if (!next.analytics) {
			visit.current?.withdraw();
			visit.current = null;
			heldRef.current = undefined;
			setHeld(undefined);
		}
	}), []);

	useEffect(() => {
		const observe = () => {
			if (visit.current?.expire(Date.now())) { heldRef.current = undefined; setHeld(undefined); }
			if (!dwell.current.observe(slide, performance.now(), ratio, unobscured && !document.hidden)) return;
			const report = visit.current?.observe("SLIDE_VIEWED", slide, getConsent().analytics, Date.now());
			if (report) reportLandingObservation(report);
		};
		observe();
		const timer = window.setInterval(observe, 100);
		return () => window.clearInterval(timer);
	}, [slide, ratio, unobscured, offerPlan]);

	return {
		held,
		freezeExposure() {
			const context = visit.current?.context(slide, getConsent().analytics, Date.now()) ?? undefined;
			heldRef.current = context;
			setHeld(context);
		},
		previewOpened() {
			// Invoked by the real sheet's completed entrance, not a press that might never open a preview.
			const context = heldRef.current;
			if (context === undefined) return;
			const report = visit.current?.observe("PREMIUM_OPENED", context.slideId, getConsent().analytics, Date.now());
			if (report) reportLandingObservation(report);
		},
	};
}
